import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import {
  Box, Typography, CircularProgress, Alert, Button, Card, IconButton,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Chip,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material'
import { fetchFavorites, fetchDislikes, clearRightSwipes, clearLeftSwipes, setActiveTab } from './favoritesActions'
import {
  Star as StarIcon,
  Image as ImageIcon,
  LocationOn as LocationIcon,
  Hotel as BedIcon,
  Bathtub as BathIcon,
  SquareFoot as AreaIcon,
  LocalParking as ParkingIcon,
  Pets as PetIcon,
  Pool as PoolIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material'
import BottomNavBar from '../../components/BottomNavBar'
import PageHeader from '../../components/PageHeader'
import SegmentedTabs from '../../components/SegmentedTabs'
import EventIcon from '@mui/icons-material/Event'
import AppointmentDialog from '../Posts/AppointmentDialog'
import ImageLightbox from '../../components/ImageLightbox'

const StyledAccordion = styled(Accordion)(() => ({
  marginBottom: 0,
  borderRadius: 0,
  boxShadow: 'none',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: 0 },
  '& .MuiAccordionSummary-root': { padding: 0, minHeight: 'auto', '&.Mui-expanded': { minHeight: 'auto' } },
  '& .MuiAccordionSummary-content': { margin: 0, '&.Mui-expanded': { margin: 0 } },
  '& .MuiAccordionDetails-root': { padding: 0 },
}))

function formatCAD(value) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency', currency: 'CAD', maximumFractionDigits: 0,
  }).format(value)
}

const Favorites = () => {
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(null) // post.id or null
  const [lightbox, setLightbox] = useState(null) // { post, index } | null
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [openClearDialog, setOpenClearDialog] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const favorites = useSelector(state => state.favorites.favorites)
  const dislikes = useSelector(state => state.favorites.dislikes)
  const loading = useSelector(state => state.favorites.loading)
  const error = useSelector(state => state.favorites.error)
  const activeTab = useSelector(state => state.favorites.activeTab)

  // Local state for instant UI update after setting appointment
  const [postsState, setPostsState] = useState({ favorites, dislikes })

  useEffect(() => {
    setPostsState({ favorites, dislikes })
  }, [favorites, dislikes])

  const handleAppointmentSuccess = (postId, appointmentId) => {
    setAppointmentDialogOpen(null)
    setPostsState(prev => ({
      ...prev,
      favorites: prev.favorites.map(post =>
        post.id === postId
          ? { ...post, swipe: { ...post.swipe, appointment: appointmentId } }
          : post
      ),
      dislikes: prev.dislikes.map(post =>
        post.id === postId
          ? { ...post, swipe: { ...post.swipe, appointment: appointmentId } }
          : post
      ),
    }))
  }

  const handleClearItems = () => {
    setOpenClearDialog(true)
  }

  const handleTabChange = (event, newValue) => {
    dispatch(setActiveTab(newValue))
  }

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  const handleConfirmClear = () => {
    if (activeTab === 'favorites') {
      dispatch(clearRightSwipes())
    } else {
      dispatch(clearLeftSwipes())
    }
    setOpenClearDialog(false)
  }

  useEffect(() => {
    dispatch(fetchFavorites())
    dispatch(fetchDislikes())
  }, [dispatch])

  const renderContent = () => {
    const items = activeTab === 'favorites' ? postsState.favorites : postsState.dislikes
    const emptyMessage = activeTab === 'favorites'
      ? 'Swipe right on posts to add them to your favorites'
      : 'Swipe left on posts to add them to your dislikes'

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>
    }

    if (items.length === 0) {
      return (
        <Box sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          p: 4,
          mx: 2,
          mt: 2,
          textAlign: 'center',
          boxShadow: '0 2px 14px rgba(0,0,0,0.07)',
        }}>
          <StarIcon sx={{ fontSize: 52, color: 'text.disabled', mb: 1.5, opacity: 0.6 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>
            Nothing here yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3, lineHeight: 1.6 }}>
            {emptyMessage}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/posts')} sx={{ borderRadius: 2, px: 3 }}>
            Browse Properties
          </Button>
        </Box>
      )
    }

    return (
      <Card sx={{ borderRadius: 0, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.10)' }}>
        {items.map((post) => (
          <StyledAccordion
            key={post.id}
            expanded={expanded === `panel-${post.id}`}
            onChange={handleChange(`panel-${post.id}`)}
          >
            {/* ── Summary: hero image + gradient (always visible) ── */}
            <AccordionSummary component="div">
              <Box sx={{ position: 'relative', width: '100%', height: 220 }}>
                {post.main_image ? (
                  <Box
                    component="img"
                    src={post.main_image}
                    alt={post.title}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <Box sx={{ width: '100%', height: '100%', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ImageIcon sx={{ fontSize: 60, color: 'action.disabled' }} />
                  </Box>
                )}

                {/* Gradient overlay */}
                <Box sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  pt: 8, pb: 1.5, px: 2,
                  pointerEvents: 'none',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.4) 55%, transparent 100%)',
                }}>
                  <Typography variant="h6" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.2, mb: 0.25 }} noWrap>
                    {post.title}
                  </Typography>
                  {post.price && (
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'rgba(255,255,255,0.92)', mb: 0.25 }}>
                      {formatCAD(post.price)}
                    </Typography>
                  )}
                  {[post.city, post.province].filter(Boolean).length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', mr: 0.5 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                        {[post.city, post.province].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Expand chevron */}
                <Box sx={{
                  position: 'absolute', bottom: 10, right: 12, pointerEvents: 'none',
                  transition: 'transform 0.2s',
                  transform: expanded === `panel-${post.id}` ? 'rotate(180deg)' : 'rotate(0deg)',
                }}>
                  <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 28 }} />
                </Box>

                {/* Appointment badge / button */}
                {activeTab === 'favorites' && post.swipe?.appointment === null && (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setAppointmentDialogOpen(post.id) }}
                    sx={{
                      position: 'absolute', top: 10, right: 10,
                      bgcolor: 'background.paper',
                      border: '2px solid #42a5f5', color: '#42a5f5',
                      width: 36, height: 36,
                      '&:hover': { bgcolor: 'rgba(66,165,245,0.1)' },
                    }}
                  >
                    <EventIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
                {activeTab === 'favorites' && post.swipe?.appointment !== null && (
                  <Box sx={{
                    position: 'absolute', top: 10, right: 10,
                    bgcolor: 'rgba(33,208,122,0.85)', borderRadius: 1, px: 1, py: 0.25,
                  }}>
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.7rem' }}>
                      Scheduled
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionSummary>

            {/* ── Details: chips + description (revealed on expand) ── */}
            <AccordionDetails>
              <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: post.description ? 1.5 : 0 }}>
                  {post.bedrooms > 0 && <Chip icon={<BedIcon />} label={`${post.bedrooms} Beds`} size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />}
                  {post.bathrooms > 0 && <Chip icon={<BathIcon />} label={`${Math.floor(post.bathrooms)} Baths`} size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />}
                  {post.square_feet > 0 && <Chip icon={<AreaIcon />} label={`${post.square_feet} sqft`} size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />}
                  {post.has_garage && <Chip icon={<ParkingIcon />} label="Parking" size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />}
                  {post.is_pet_friendly && <Chip icon={<PetIcon />} label="Pet Friendly" size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />}
                  {post.has_pool && <Chip icon={<PoolIcon />} label="Pool" size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />}
                  {post.preferences?.map((p, i) => (
                    <Chip key={`${p}-${i}`} label={p} size="small" variant="outlined"
                      sx={{ borderRadius: 1.5, fontSize: '0.7rem', bgcolor: 'rgba(25,118,210,0.08)', color: 'primary.dark' }} />
                  ))}
                </Box>
                {post.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: '0.85rem' }}>
                    {post.description}
                  </Typography>
                )}

                {post.additional_images?.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      More Photos
                    </Typography>
                    <Box sx={{
                      display: 'flex', gap: 1, overflowX: 'auto',
                      pb: 0.5,
                      '&::-webkit-scrollbar': { height: 4 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
                    }}>
                      {post.additional_images.map((img, i) => (
                        <Box
                          key={img.id ?? i}
                          component="img"
                          src={img.image}
                          alt={img.description || 'Property photo'}
                          onClick={() => setLightbox({ post, index: i })}
                          sx={{
                            height: 100, width: 150, flexShrink: 0,
                            objectFit: 'cover', borderRadius: 1.5,
                            cursor: 'pointer',
                            transition: 'opacity 0.15s',
                            '&:hover': { opacity: 0.85 },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {appointmentDialogOpen === post.id && (
                <AppointmentDialog
                  open={true}
                  onClose={() => setAppointmentDialogOpen(null)}
                  swipeId={post.id}
                  onSuccess={appointmentId => handleAppointmentSuccess(post.id, appointmentId)}
                />
              )}
            </AccordionDetails>
          </StyledAccordion>
        ))}
      </Card>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <PageHeader
        title={activeTab === 'favorites' ? 'Favorites' : 'Dislikes'}
        subtitle={`${activeTab === 'favorites' ? favorites.length : dislikes.length} properties`}
        action={
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleClearItems}
            disabled={(activeTab === 'favorites' ? favorites.length === 0 : dislikes.length === 0) || loading}
            sx={{ fontSize: '0.75rem', borderRadius: 10, mt: 0.5, flexShrink: 0 }}
          >
            Clear All
          </Button>
        }
      />

      <SegmentedTabs
        value={activeTab}
        onChange={(tab) => handleTabChange(null, tab)}
        sx={{ mx: 2, mt: 2, mb: 2 }}
        tabs={[
          { key: 'favorites', label: `Liked (${favorites.length})` },
          { key: 'dislikes', label: `Disliked (${dislikes.length})` },
        ]}
      />

      {/* Scrollable Content */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        bgcolor: 'background.default',
        pb: 'calc(80px + env(safe-area-inset-bottom, 0px))',
      }}>
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : (
          renderContent()
        )}
      </Box>

      <Dialog
        open={openClearDialog}
        onClose={() => setOpenClearDialog(false)}
      >
        <DialogTitle>
          Clear All {activeTab === 'favorites' ? 'Favorites' : 'Dislikes'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear all your {activeTab === 'favorites' ? 'favorite' : 'disliked'} listings? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenClearDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmClear} color="error" autoFocus>
            Clear All
          </Button>
        </DialogActions>
      </Dialog>

      <BottomNavBar />

      <ImageLightbox
        images={lightbox?.post?.additional_images ?? []}
        initialIndex={lightbox?.index ?? 0}
        open={lightbox !== null}
        onClose={() => setLightbox(null)}
      />
    </Box>
  )
}

export default Favorites
